import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { format, subDays, startOfDay, endOfDay, parseISO } from "date-fns";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar } from 'primereact/calendar';
import { Skeleton } from 'primereact/skeleton';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { GetEnquiry } from "../../api/enquiryService";
import { useAuth } from "../../context/AuthContext";
import "../../css/Enquiry/enquiry_history.css";
import CustomerSelection from "../../components/Common/CustomerSelection";

export default function EnquiryPage() {
    document.title = "My Enquiries | Meera Jewellery House Admin";

    const { user } = useAuth();
    const navigate = useNavigate();
    const [searchText, setSearchText] = useState("");
    const [dates, setDates] = useState(null);
    const [showLoader, setShowLoader] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [enquiries, setEnquiries] = useState([]);
    const [pagination, setPagination] = useState(null);
    const [progress, setProgress] = useState(0);
    const [dataReady, setDataReady] = useState(false);

    const formatDateKeepTime = (dateTime) => {
        if (!dateTime) return "-";
        const date = parseISO(dateTime);
        return format(date, "dd MMM yyyy hh:mm a");
    };

    const fetchGetEnquiryDetail = async () => {
        setShowLoader(true);
        setDataReady(false);
        try {
            const payload = {
                Party_ID: user?.SelectedCustomer_ID || "0",
                EnquirySearch: "",
                FromDate: "",
                ToDate: "",
                PageNo: "1",
                PageSize: "100",
                OrderCategory: "ENQUIRY",
            };
            const response = await GetEnquiry(payload);

            const messageObj = response?.Message?.[0] || response?.ZMESSAGE?.[0];
            if (messageObj?.RETURNCODE !== "1") {
                setErrorMessage(messageObj?.RETURNMESSAGE || "Something went wrong.");
                return;
            }

            const data = response?.Enquiry || response?.ROW || [];
            const paginationData = response?.Pagination?.[0] || response?.ROW1?.[0] || null;

            setEnquiries(data);
            setPagination(paginationData);
        } catch (error) {
            console.error("GetEnquiry API Error", error);
        } finally {
            setProgress(100);
            setTimeout(() => {
                setShowLoader(false);
                setDataReady(true);
            }, 400);
        }
    };

    useEffect(() => {
        fetchGetEnquiryDetail();
    }, [user?.SelectedCustomer_ID]);

    const filteredData = enquiries
        .filter((item) => {
            if (!dates || !dates[0] || !dates[1]) return true;
            const itemDate = new Date(item.ENQUIRYDATE || "");
            const start = startOfDay(dates[0]);
            const end = endOfDay(dates[1]);
            return itemDate >= start && itemDate <= end;
        })
        .filter((item) => {
            const search = searchText.toLowerCase();
            return (
                (item.ENQUIRYNO || "").toLowerCase().includes(search) ||
                (item.STATUSCOMMENT || "").toLowerCase().includes(search) ||
                (item.STATUS || "").toLowerCase().includes(search) ||
                String(item.STYLECOUNT ?? "").includes(search)
            );
        });
    
    const groupedData = useMemo(() => {
        const groups = {};
        filteredData.forEach(item => {
            const customerName = (item.CUSTOMERNAME || "Unknown Customer").trim();
            if (!groups[customerName]) {
                groups[customerName] = [];
            }
            groups[customerName].push(item);
        });
        return groups;
    }, [filteredData]);



    return (
        <div className="enquiry-history-page w-100">
            <div className="enquiry-header-card">
                <div className="enquiry-header-top-row">
                    <h1 className="enquiry-page-title">
                        My Enquiries <span>({pagination?.Total_Record || filteredData.length} records)</span>
                    </h1>

                    <div className="enquiry-header-actions-right">
                        <div className="enquiry-calendar-wrapper">
                            <Calendar
                                value={dates}
                                onChange={(e) => setDates(e.value)}
                                selectionMode="range"
                                readOnlyInput
                                hideOnRangeSelection
                                placeholder="Select Date Range"
                                className="enquiry-calendar-custom"
                            />
                        </div>

                        <div className="enquiry-search-input-wrapper">
                            <input
                                type="text"
                                className="enquiry-search-input"
                                placeholder="Search..."
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                            />
                            <i className="pi pi-search" style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '0.9rem' }} />
                        </div>

                        <div className="cart-header-actions-left">
                            <CustomerSelection />
                        </div>

                        <button className="back-btn" onClick={() => navigate('/LiveInventory')} >
                            <i className="pi pi-arrow-left" />
                        </button>
                    </div>
                </div>
            </div>

            <div className="enquiry-grid-row row g-3">
                <AnimatePresence>
                    {!dataReady ? (
                        Array.from({ length: 6 }).map((_, i) => (
                            <div key={i} className="enquiry-col col-xxl-2 col-xl-2 col-lg-3 col-md-4 col-sm-6 col-12">
                                <div className="enquiry-card-compact">
                                    <div className="enquiry-card-header">
                                        <div className="enquiry-no-compact"><Skeleton width="100px" height="1.2rem" /></div>
                                        <Skeleton width="60px" height="1.2rem" borderRadius="20px" />
                                    </div>
                                    <div className="enquiry-card-body">
                                        <div className="enquiry-date-compact"><Skeleton width="130px" height="0.8rem" style={{ marginBottom: '1rem' }} /></div>
                                        <div className="card-label-row">
                                            <Skeleton width="40%" height="0.8rem" />
                                        </div>
                                        <div className="card-label-row" style={{ marginTop: '0.8rem' }}>
                                            <strong><Skeleton width="100%" height="1.5rem" /></strong>
                                        </div>
                                    </div>
                                    <div className="enquiry-card-footer">
                                        <Skeleton width="100%" height="34px" borderRadius="6px" />
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        (Number(user?.SelectedCustomer_ID || 0) === 0) ? (
                            <div className="accordion-full-width w-100">
                                <Accordion multiple={true} className="customer-accordion">
                                    {Object.entries(groupedData).map(([customerName, items]) => (
                                        <AccordionTab
                                            key={customerName}
                                            header={
                                                <div className="accordion-header-content">
                                                    <span>{customerName}</span>
                                                    <span className="customer-badge">{items.length} Enquiries</span>
                                                </div>
                                            }
                                        >
                                            <div className="enquiry-grid-row row g-3">
                                                {items.map((item, index) => (
                                                    <motion.div
                                                        key={item.ENQUIRY_ID}
                                                        layout
                                                        initial={{ opacity: 0, scale: 0.9 }}
                                                        animate={{ opacity: 1, scale: 1 }}
                                                        exit={{ opacity: 0, scale: 0.9 }}
                                                        transition={{ duration: 0.3, delay: index * 0.03 }}
                                                        className="enquiry-col col-xxl-2 col-xl-2 col-lg-3 s col-sm-6 col-12"
                                                    >
                                                        <div className="enquiry-card-compact">
                                                            <div className="enquiry-card-header">
                                                                <h3 className="enquiry-no-compact">{item.ENQUIRYNO || "N/A"}</h3>
                                                                <span className={`status-badge-compact status-${(item.STATUS || "default").toLowerCase()}`}>
                                                                    {item.STATUS || "PENDING"}
                                                                </span>
                                                            </div>
                                                            <div className="enquiry-card-body">
                                                                <div className="enquiry-date-compact">
                                                                    {formatDateKeepTime(item.ENTRYDATE)}
                                                                </div>
                                                                <div className="card-label-row">
                                                                    <strong>T.Style.:</strong> {item.STYLECOUNT || 0}
                                                                </div>
                                                                <div className="card-label-row">
                                                                    <strong>Remark:</strong> <span className="text-muted small d-block text-truncate" style={{ maxWidth: '100%', fontSize: "14px" }}>
                                                                        {item.STATUSCOMMENT || "-"}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                            <div className="enquiry-card-footer">
                                                                <Link
                                                                    to={`/enquiry-detail/${item.ENQUIRY_ID}`}
                                                                    state={{ enquiryNo: item.ENQUIRYNO, remark: item.STATUSCOMMENT }}
                                                                    className="view-detail-btn-full"
                                                                >
                                                                    View Enquiry Detail
                                                                </Link>
                                                            </div>
                                                        </div>
                                                    </motion.div>
                                                ))}
                                            </div>
                                        </AccordionTab>
                                    ))}
                                </Accordion>
                            </div>
                        ) : (
                            filteredData.map((item, index) => (
                                <motion.div
                                    key={item.ENQUIRY_ID}
                                    layout
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.9 }}
                                    transition={{ duration: 0.3, delay: index * 0.03 }}
                                    className="enquiry-col col-xxl-2 col-xl-2 col-lg-3 col-md-4 col-sm-6 col-12"
                                >
                                    <div className="enquiry-card-compact">
                                        <div className="enquiry-card-header">
                                            <h3 className="enquiry-no-compact">{item.ENQUIRYNO || "N/A"}</h3>
                                            <span className={`status-badge-compact status-${(item.STATUS || "default").toLowerCase()}`}>
                                                {item.STATUS || "PENDING"}
                                            </span>
                                        </div>
                                        <div className="enquiry-card-body">
                                            <div className="enquiry-date-compact">
                                                {formatDateKeepTime(item.ENTRYDATE)}
                                            </div>
                                            <div className="card-label-row">
                                                <strong>T.Style.:</strong> {item.STYLECOUNT || 0}
                                            </div>
                                            <div className="card-label-row">
                                                <strong>Remark:</strong> <span className="text-muted small d-block text-truncate" style={{ maxWidth: '100%', fontSize: "14px" }}>
                                                    {item.STATUSCOMMENT || "-"}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="enquiry-card-footer">
                                            <Link
                                                to={`/enquiry-detail/${item.ENQUIRY_ID}`}
                                                state={{ enquiryNo: item.ENQUIRYNO, remark: item.STATUSCOMMENT }}
                                                className="view-detail-btn-full"
                                            >
                                                View Enquiry Detail
                                            </Link>
                                        </div>
                                    </div>
                                </motion.div>
                            ))
                        )
                    )}
                </AnimatePresence>

                {dataReady && filteredData.length === 0 && (
                    <div className="empty-cart-premium w-100">
                        <div className="empty-cart-visual">
                            <i className="pi pi-envelope" />
                        </div>
                        <div className="empty-cart-content">
                            <h2 className="empty-cart-title">No Enquiries Found</h2>
                            <p className="empty-cart-text">
                                Try adjusting your filters or date range, or load all records to see the history.
                            </p>
                            <button className="btn-primary" onClick={() => { setDates(null); setSearchText(""); fetchGetEnquiryDetail(); }}>
                                <i className="pi pi-refresh me-2" />
                                Load All Records
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}